import type { NuxtApp } from 'nuxt/app';

/**
 * Composable for tracking events to Google Ads, GTM, and Meta Pixel
 * Uses @nuxt/scripts for proper script loading and tracking
 *
 * Consent Mode v2: "Accept all" grants all four signals so Tag Assistant / GA4 behave as expected.
 * Essential-only keeps everything denied (measurement cookies blocked).
 */

const GTM_CONTAINER_ID = 'GTM-5S7GNFN2';

/** Consent default before user choice (EU-style) */
function consentDefaultDenied() {
    return {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500,
    } as const;
}

/** Full grant when user clicks "Accept all" (aligns with Nuxt Scripts + Tag Assistant expectations) */
function consentUpdateAllGranted() {
    return {
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        ad_storage: 'granted',
        analytics_storage: 'granted',
    } as const;
}

function consentUpdateAllDenied() {
    return {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: 'denied',
    } as const;
}

/**
 * gtag queues commands as dataLayer.push(arguments), not as a single array.
 * Use when window.gtag is not set yet (before gtag.js finishes loading).
 */
function pushGtagCommand(...args: unknown[]) {
    const w = window as unknown as { dataLayer?: unknown[] };
    if (!w.dataLayer) {
        w.dataLayer = [];
    }
    const dl = w.dataLayer;
    function relay(this: unknown) {
        dl.push(arguments as unknown as never);
    }
    Reflect.apply(relay, null, args);
}

type TrackingApi = {
    hasAcceptedCookies: ComputedRef<boolean>;
    gaInstance: ReturnType<typeof useScriptGoogleAnalytics>;
    gtmInstance: ReturnType<typeof useScriptGoogleTagManager>;
    metaPixelInstance: ReturnType<typeof useScriptMetaPixel>;
    trackPageView: (title?: string, path?: string, options?: { skipConsentCheck?: boolean }) => Promise<void>;
    /** Call after user accepts analytics cookies — fires consent + immediate page_view */
    notifyAnalyticsConsentAccepted: () => Promise<void>;
    sendConversionEvent: () => Promise<void>;
};

/** Not serialized (unlike useState) — must not freeze consent reactivity across hydration */
const TRACKING_NUXT_APP_KEY = '__radimedTrackingApi' as const;
type NuxtAppWithTracking = NuxtApp & { [TRACKING_NUXT_APP_KEY]?: TrackingApi };

function createTracking(): TrackingApi {
    const { hasAcceptedAnalytics } = useCookieConsent();

    // GTM must run before gtm.start with consent defaults; init here (not nuxt.config) so
    // onBeforeGtmStart is applied — global registry cannot pass functions from config.
    const gtmInstance = useScriptGoogleTagManager({
        id: GTM_CONTAINER_ID,
        onBeforeGtmStart: (gtag) => {
            gtag('consent', 'default', consentDefaultDenied());
            if (hasAcceptedAnalytics.value) {
                gtag('consent', 'update', consentUpdateAllGranted());
            }
        },
    });

    const gaInstance = useScriptGoogleAnalytics({
        id: 'AW-17596457645',
        scriptOptions: {
            trigger: 'onNuxtReady',
        },
        onBeforeGtagStart: (gtagFn) => {
            gtagFn('consent', 'default', consentDefaultDenied());
            if (hasAcceptedAnalytics.value) {
                gtagFn('consent', 'update', consentUpdateAllGranted());
            }
        },
    });

    const metaPixelInstance = useScriptMetaPixel({
        id: '1409128877490270',
        scriptOptions: {
            trigger: 'onNuxtReady',
        },
    });

    /**
     * Nuxt pauses head DOM updates until the first suspense resolve, and Unhead drops any
     * script pushed while that render is still running. The Meta Pixel tag is registered in
     * that window, so fbevents.js never loads and every fbq() call stays in the stub queue.
     * After the in-flight render finishes, ask Unhead to render again.
     */
    const ensureMetaPixelScriptRendered = async () => {
        if (!import.meta.client) {
            return;
        }

        const head = injectHead() as {
            invalidate: () => void;
            _domUpdatePromise?: Promise<unknown>;
        };
        const isMounted = () => !!document.querySelector('script[data-hid="metaPixel"]');

        for (let attempt = 0; attempt < 4 && !isMounted(); attempt++) {
            const pending = head._domUpdatePromise;
            if (pending) {
                await pending.catch(() => undefined);
            }
            if (isMounted()) {
                return;
            }

            head.invalidate();
            await new Promise((resolve) => setTimeout(resolve, 0));
            const next = head._domUpdatePromise;
            if (next) {
                await next.catch(() => undefined);
            }
        }
    };

    const pushGtagConsentUpdate = async (accepted: boolean) => {
        if (!import.meta.client) {
            return;
        }
        const payload = accepted ? consentUpdateAllGranted() : consentUpdateAllDenied();
        await Promise.all([gtmInstance.load(), gaInstance.load()]).catch(() => {
            /* scripts may already be loaded */
        });

        const w = window as Window & { gtag?: (...args: unknown[]) => void };
        if (typeof w.gtag === 'function') {
            w.gtag('consent', 'update', payload);
        } else {
            pushGtagCommand('consent', 'update', payload);
        }
    };

    watch(hasAcceptedAnalytics, (accepted) => void pushGtagConsentUpdate(accepted), {
        immediate: true,
    });

    watch(
        hasAcceptedAnalytics,
        (accepted) => {
            if (accepted) {
                metaPixelInstance.proxy.fbq('consent', 'grant');
            } else {
                metaPixelInstance.proxy.fbq('consent', 'revoke');
            }
        },
        { immediate: true }
    );

    const trackPageView = async (title?: string, path?: string, options?: { skipConsentCheck?: boolean }) => {
        if (!options?.skipConsentCheck && !hasAcceptedAnalytics.value) {
            return;
        }

        await ensureMetaPixelScriptRendered();

        await Promise.all([gaInstance.load(), gtmInstance.load(), metaPixelInstance.load()]).catch(() => {
            /* idempotent */
        });

        const pageTitle = title ?? '';
        const pagePath = path ?? '';

        const w = window as Window & { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] };
        const proxyGtag = gaInstance.proxy.gtag;
        if (typeof proxyGtag === 'function') {
            proxyGtag('event', 'page_view', {
                page_title: pageTitle,
                page_path: pagePath,
            });
        } else if (typeof w.gtag === 'function') {
            w.gtag('event', 'page_view', {
                page_title: pageTitle,
                page_path: pagePath,
            });
        }

        const { proxy } = gtmInstance;
        const dl = proxy.dataLayer ?? w.dataLayer;
        if (dl && typeof dl.push === 'function') {
            dl.push({
                event: 'pageview',
                page_title: pageTitle,
                page_path: pagePath,
            });
        }

        metaPixelInstance.proxy.fbq('track', 'PageView');
    };

    /** Call right after setting the consent cookie to `1` (e.g. Accept all). */
    const notifyAnalyticsConsentAccepted = async () => {
        if (!import.meta.client) {
            return;
        }
        await pushGtagConsentUpdate(true);
        await nextTick();
        await trackPageView(
            typeof document !== 'undefined' ? document.title : undefined,
            typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
            { skipConsentCheck: true }
        );
    };

    const sendConversionEvent = async () => {
        try {
            await gaInstance.load();

            gaInstance.proxy.gtag('event', 'conversion', {
                send_to: 'AW-17596457645/9ufjCPj0-aAbEK3F0sZB',
                value: 1.0,
                currency: 'EUR',
            });
        } catch {
            /* ignore */
        }

        try {
            await ensureMetaPixelScriptRendered();
            await metaPixelInstance.load();

            metaPixelInstance.proxy.fbq('track', 'Schedule', {
                content_name: 'Appointment Scheduled',
                content_category: 'Appointment',
                status: 'completed',
                currency: 'EUR',
                value: 1.0,
            });

            if (import.meta.client) {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set('success', '1');

                window.history.pushState({}, '', currentUrl.toString());

                metaPixelInstance.proxy.fbq('track', 'PageView');
            }
        } catch {
            /* ignore */
        }
    };

    return {
        hasAcceptedCookies: hasAcceptedAnalytics,
        gaInstance,
        gtmInstance,
        metaPixelInstance,
        trackPageView,
        notifyAnalyticsConsentAccepted,
        sendConversionEvent,
    };
}

/**
 * One instance per Nuxt app (not useState — that serializes to the SSR payload and breaks
 * ComputedRefs / consent reactivity after hydration, so "Accept all" never flips hasAcceptedAnalytics).
 */
export const useTracking = () => {
    const nuxtApp = useNuxtApp() as NuxtAppWithTracking;
    if (!nuxtApp[TRACKING_NUXT_APP_KEY]) {
        nuxtApp[TRACKING_NUXT_APP_KEY] = createTracking();
    }
    return nuxtApp[TRACKING_NUXT_APP_KEY]!;
};
